import type { StockMachineDataDto } from '@api/machines'
import { truncateText } from '@common/utils'
import {
  type ColumnCellTemplateData,
  MolsonBox,
  MolsonDxGrid,
  MolsonTypography
} from '@components/reusable'
import { type molsonTheme } from '@theme'
import { molsonStyled, molsonUseTheme } from '@theme/customMuiExports'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomMolsonAccordion } from '../styled-components/styles'

const CustomDxGrid = molsonStyled(MolsonDxGrid)({
  '& .dx-datagrid-header-panel': {
    '& > div': {
      borderRadius: '16px',
      marginBottom: 0
    },
    paddingBottom: '20px'
  }
})

type ReplaceEquipmentGridProps = {
  data?: StockMachineDataDto[]
  showFilters?: boolean
  showPagination?: boolean
  showReplaceWithSuggestion?: boolean
  showSearchField?: boolean
  storageKey?: string
  onRowClick?: (itemId: number) => void
}

type RenderSimpleTextGridCellProps = {
  params: ColumnCellTemplateData
  theme: typeof molsonTheme
  isOutOfStockItem?: boolean
  isLocationOrStockCell?: boolean
  isConfigurationCell?: boolean
}

const maxTextLength = 40

export const ReplaceEquipmentGrid = ({
  data,
  showFilters = true,
  showPagination = true,
  showReplaceWithSuggestion = true,
  showSearchField = true,
  storageKey,
  onRowClick
}: ReplaceEquipmentGridProps) => {
  const theme = molsonUseTheme()

  const { t } = useTranslation('')
  const commonTranslationkey = 'common'
  const nonCommonTranslationKey =
    'newQuote.itemsTab.cards.machineCard.replaceMachineDialog'

  const isOutOfStockItem = useMemo(
    () => data?.length === 1 && data[0]?.status === 'Sold',
    [data]
  )

  const handleRowClick = (itemId: number) => {
    if (isOutOfStockItem) {
      return
    }
    onRowClick?.(itemId)
  }

  const renderGridCell = ({
    params,
    theme,
    isOutOfStockItem,
    isLocationOrStockCell,
    isConfigurationCell
  }: RenderSimpleTextGridCellProps) => {
    const configurationCellLabel =
      params.text.length > maxTextLength ? params.text.slice(maxTextLength) : ''

    return (
      <>
        {isConfigurationCell ? (
          <CustomMolsonAccordion
            sx={{
              '.MuiTypography-root': {
                fontWeight: 'normal'
              },
              background: 'transparent'
            }}
            primaryText={truncateText(params.text, maxTextLength)}
            primaryTextVariant="body1"
            summarySx={{ borderBottom: 'none', padding: 0 }}
          >
            <MolsonBox
              sx={{
                whiteSpace: 'pre-wrap'
              }}
              display="flex"
              flexWrap="wrap"
            >
              {configurationCellLabel}
            </MolsonBox>
          </CustomMolsonAccordion>
        ) : (
          <MolsonTypography
            color={isOutOfStockItem ? theme.palette.primary.error : ''}
            fontWeight={isLocationOrStockCell ? 500 : 'normal'}
            whiteSpace={isLocationOrStockCell ? 'break-spaces' : 'normal'}
          >
            {params.text}
          </MolsonTypography>
        )}
      </>
    )
  }

  return (
    <MolsonBox position="relative" width="100%">
      {showReplaceWithSuggestion && (
        <MolsonBox position="absolute" top={0} width="fit-content" zIndex={2}>
          <MolsonTypography fontWeight="bold">
            {t(`${nonCommonTranslationKey}.replaceWith`)}
          </MolsonTypography>
          <MolsonTypography>
            {t(`${nonCommonTranslationKey}.selectReplacement`)}
          </MolsonTypography>
        </MolsonBox>
      )}
      <CustomDxGrid
        className={isOutOfStockItem ? 'table-border-red' : ''}
        columnsProps={[
          {
            allowHeaderFiltering: true,
            caption: t(`${commonTranslationkey}.unitNumber`) || '',
            cellRender: params => renderGridCell({ params, theme }),
            dataField: 'unitNumber',
            width: 250
          },
          {
            allowHeaderFiltering: true,
            caption: t(`${commonTranslationkey}.status`) || '',
            cellRender: params =>
              renderGridCell({
                params,
                theme,
                isOutOfStockItem
              }),
            dataField: 'status',
            width: 170
          },
          {
            allowHeaderFiltering: true,
            caption: t(`${commonTranslationkey}.location`) || '',
            cellRender: params =>
              renderGridCell({
                params,
                theme,
                isLocationOrStockCell: true
              }),
            dataField: 'location',
            width: 250
          },
          {
            alignment: 'right',
            allowHeaderFiltering: true,
            caption: t(`${commonTranslationkey}.daysInStock`) || '',
            cellRender: params =>
              renderGridCell({
                params,
                theme,
                isLocationOrStockCell: true
              }),
            dataField: 'daysInStock',
            headerFilter: {
              groupInterval: 10000
            },
            width: 170
          },
          {
            alignment: 'left',
            caption: t(`${commonTranslationkey}.configuration`) || '',
            cellRender: params =>
              renderGridCell({
                params,
                theme,
                isConfigurationCell: true
              }),
            dataField: 'configuration',
            minWidth: 250
          }
        ]}
        filterRow={{
          visible: false
        }}
        headerFilter={{
          visible: showFilters
        }}
        onCellPrepared={e =>
          e.rowType === 'data' &&
          e.cellElement.style.setProperty('cursor', 'pointer', 'important')
        }
        pagerProps={{
          visible: showPagination
        }}
        remoteOperations={true}
        scrolling={{
          useNative: true
        }}
        selection={{
          mode: isOutOfStockItem ? 'none' : 'single'
        }}
        searchPanelProps={{
          highlightSearchText: true,
          visible: showSearchField
        }}
        stateStoringProps={{
          enabled: true,
          storageKey: storageKey
        }}
        allowColumnResizing={true}
        columnAutoWidth={true}
        dataSource={data}
        filterSyncEnabled={false}
        headerFilterProps={{ visible: true }}
        onRowClick={value => handleRowClick(value.data.stockId)}
        noDataText="no data"
      />
    </MolsonBox>
  )
}
